/**
 * Tenant validation schemas.
 * Defines tenant configuration, settings, and environment configuration.
 */
import { z } from "zod";
import { environmentSchema } from "./environment";
// ============================================================================
// Common Patterns
// ============================================================================
/** Currency code pattern (ISO 4217) */
const currencyCodePattern = /^[A-Z]{3}$/;
/** Locale code pattern (e.g., en-US, en-AU) */
const localeCodePattern = /^[a-z]{2}(-[A-Z]{2})?$/;
/** Hex color pattern */
const hexColorPattern = /^#[0-9A-Fa-f]{6}$/;
// ============================================================================
// Tenant Identifiers
// ============================================================================
/** Tenant ID schema - prefixed ID format (e.g., tnt_xxx...) */
export const TenantIdSchema = z
    .string()
    .min(8)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, "Invalid tenant ID format");
/** Tenant external ID schema */
export const TenantExternalIdSchema = z
    .string()
    .min(21)
    .max(21)
    .regex(/^[A-Za-z0-9_-]+$/, "Invalid tenant external ID format");
/** Tenant slug schema */
export const TenantSlugSchema = z
    .string()
    .min(1)
    .max(63)
    .regex(/^[a-z][a-z0-9-]*[a-z0-9]$/, "Slug must be lowercase alphanumeric with hyphens");
// ============================================================================
// Tenant Status
// ============================================================================
/** Tenant status schema */
export const TenantStatusSchema = z.enum(["active", "suspended", "archived", "deleted"]);
// ============================================================================
// Tenant Features
// ============================================================================
/** Tenant-level feature flags */
export const TenantFeatureSchema = z.enum([
    "ai_suggestions",
    "collaborative_editing",
    "advanced_analytics",
    "custom_branding",
    "api_access",
    "csv_upload",
    "json_upload",
    "form_upload",
]);
// ============================================================================
// Tenant Branding Configuration
// ============================================================================
/** Tenant branding configuration */
export const TenantBrandingConfigSchema = z
    .object({
    logoUrl: z.string().url().optional(),
    faviconUrl: z.string().url().optional(),
    primaryColor: z.string().regex(hexColorPattern, "Must be a valid hex color").optional(),
    accentColor: z.string().regex(hexColorPattern, "Must be a valid hex color").optional(),
    fontFamily: z.string().min(1).max(100).optional(),
})
    .strict();
// ============================================================================
// Tenant Settings
// ============================================================================
/** Tenant settings configuration */
export const TenantSettingsSchema = z
    .object({
    defaultCurrency: z.string().regex(currencyCodePattern, "Invalid ISO 4217 currency code"),
    defaultLocale: z.string().regex(localeCodePattern, "Invalid locale code"),
    features: z.array(TenantFeatureSchema),
    maxVerticals: z.number().int().min(1).max(100).optional(),
    maxProductsPerVertical: z.number().int().min(1).max(100000).optional(),
    brandingConfig: TenantBrandingConfigSchema.optional(),
})
    .strict();
// ============================================================================
// Tenant Entity
// ============================================================================
/** Tenant entity stored in D1 */
export const TenantSchema = z
    .object({
    id: TenantIdSchema,
    externalId: TenantExternalIdSchema,
    name: z.string().min(1).max(255),
    slug: TenantSlugSchema,
    status: TenantStatusSchema,
    settings: TenantSettingsSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
// ============================================================================
// Tenant Context
// ============================================================================
/** Tenant resolution source */
export const TenantResolutionSourceSchema = z.enum(["jwt", "header", "hostname"]);
/** Tenant context resolved per-request */
export const TenantContextSchema = z
    .object({
    tenantId: TenantIdSchema,
    tenantSlug: TenantSlugSchema,
    tenantName: z.string(),
    status: TenantStatusSchema,
    environment: environmentSchema,
    resolvedFrom: TenantResolutionSourceSchema,
})
    .strict();
/** Input for tenant resolution */
export const TenantResolutionInputSchema = z
    .object({
    jwtTenantId: z.string().optional(),
    jwtEnvironment: z.string().optional(),
    headerTenantId: z.string().optional(),
    headerEnvironment: z.string().optional(),
    hostname: z.string().optional(),
})
    .strict();
// ============================================================================
// Tenant Configuration (Complete)
// ============================================================================
/** Complete tenant configuration including settings and metadata */
export const TenantConfigSchema = z
    .object({
    id: TenantIdSchema,
    externalId: TenantExternalIdSchema,
    name: z.string().min(1).max(255),
    slug: TenantSlugSchema,
    status: TenantStatusSchema,
    settings: TenantSettingsSchema,
    verticals: z.array(z.string()).optional(),
    environments: z.array(environmentSchema).optional(),
    metadata: z.record(z.unknown()).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().optional(),
})
    .strict();
// ============================================================================
// Environment Configuration
// ============================================================================
/** Log level */
export const LogLevelSchema = z.enum(["debug", "info", "warn", "error"]);
/** Environment-specific configuration */
export const EnvironmentConfigSchema = z
    .object({
    environment: environmentSchema,
    baseUrl: z.string().url(),
    apiUrl: z.string().url(),
    logLevel: LogLevelSchema,
    features: z.record(z.boolean()),
})
    .strict();
/** Environment configuration per tenant */
export const TenantEnvironmentConfigSchema = z
    .object({
    tenantId: TenantIdSchema,
    environment: environmentSchema,
    config: EnvironmentConfigSchema,
    secrets: z
        .object({
        apiKey: z.string().optional(),
        webhookSecret: z.string().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
// ============================================================================
// Tenant Creation/Update Inputs
// ============================================================================
/** Tenant creation input schema */
export const CreateTenantInputSchema = z
    .object({
    name: z.string().min(1).max(255),
    slug: TenantSlugSchema.optional(),
    adminEmail: z.string().email().optional(),
    settings: TenantSettingsSchema.partial().optional(),
})
    .strict();
/** Tenant update input schema */
export const UpdateTenantInputSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    status: TenantStatusSchema.optional(),
    settings: TenantSettingsSchema.partial().optional(),
})
    .strict();
// ============================================================================
// Legacy Exports (for backward compatibility)
// ============================================================================
/** @deprecated Use TenantIdSchema instead */
export const tenantIdSchema = TenantIdSchema;
/** @deprecated Use TenantStatusSchema instead */
export const tenantStatusSchema = TenantStatusSchema;
/** @deprecated Use CreateTenantInputSchema instead */
export const createTenantSchema = CreateTenantInputSchema;
/** @deprecated Use UpdateTenantInputSchema instead */
export const updateTenantSchema = UpdateTenantInputSchema;
